import { differenceInDays, endOfToday } from "date-fns";

import { config } from "config/config";
import { IPlanLimits } from "store/entities/application-settings/interfaces";
import { ILicenseSettings } from "store/entities/license";
import { IRecurlyCoupon, RecurlyCouponType } from "store/entities/recurly";
import { BillingCycle, ISubscription, Plan } from "store/entities/subscriptions";

export const COUPON_COOKIE_OPTIONS = {
  domain: config.couponCookieDomain,
  path: "/",
};

export interface IPlanMeta {
  plan: Plan;
  name: string;
  price: { [key in BillingCycle]: number };
}

const planNames = {
  [Plan.Team]: "Team",
  [Plan.Business]: "Business",
  [Plan.Enterprise]: "Enterprise",
};

const teamPlan: IPlanMeta = {
  plan: Plan.Team,
  name: planNames[Plan.Team],
  price: {
    [BillingCycle.Monthly]: 34,
    [BillingCycle.Quarterly]: 34,
    [BillingCycle.SemiAnnually]: 34,
    [BillingCycle.Annually]: 29,
    [BillingCycle.TwoYears]: 29,
    [BillingCycle.ThreeYears]: 29,
  },
};

const businessPlan: IPlanMeta = {
  plan: Plan.Business,
  name: planNames[Plan.Business],
  price: {
    [BillingCycle.Monthly]: 59,
    [BillingCycle.Quarterly]: 59,
    [BillingCycle.SemiAnnually]: 59,
    [BillingCycle.Annually]: 50,
    [BillingCycle.TwoYears]: 50,
    [BillingCycle.ThreeYears]: 50,
  },
};

const enterprisePlan: IPlanMeta = {
  plan: Plan.Enterprise,
  name: planNames[Plan.Enterprise],
  price: {
    [BillingCycle.Monthly]: +Infinity,
    [BillingCycle.Quarterly]: +Infinity,
    [BillingCycle.SemiAnnually]: +Infinity,
    [BillingCycle.Annually]: +Infinity,
    [BillingCycle.TwoYears]: +Infinity,
    [BillingCycle.ThreeYears]: +Infinity,
  },
};

const subscriptionPlans: IPlanMeta[] = [teamPlan, businessPlan, enterprisePlan];

export function getSubscriptionPlans(plans?: Plan[]): IPlanMeta[] {
  return plans ? subscriptionPlans.filter(plan => plans.includes(plan.plan)) : subscriptionPlans;
}

export function getSubscriptionPlanMeta(plan: Plan): IPlanMeta {
  return subscriptionPlans.find(planMeta => plan === planMeta.plan) as IPlanMeta;
}

export function getPlanName(plan: Plan): string {
  return planNames[plan];
}

const planWeights = {
  [Plan.Team]: 1,
  [Plan.Business]: 2,
};

export const getNextPlanHash = (currentPlan: Plan): string => {
  switch (currentPlan) {
    case Plan.Team:
      return "#upgrade-business";
    case Plan.Business:
      return "#upgrade-enterprise";
    default:
      return "";
  }
};

function calculateAgentPrice(subscription: ISubscription): number {
  if (typeof subscription.pricePerAgent === "number") {
    const priceInCents = subscription.pricePerAgent;

    return priceInCents / 100;
  }

  return getSubscriptionPlanMeta(subscription.plan).price[subscription.billingCycle] * subscription.billingCycle;
}

export function calculatePrice(subscription: ISubscription, coupon?: IRecurlyCoupon | null): number {
  let basePrice = subscription.agents * calculateAgentPrice(subscription);
  if (coupon) {
    if (coupon.discount.type === RecurlyCouponType.Percent) {
      basePrice -= (basePrice * coupon.discount.value) / 100;
    } else {
      basePrice = Math.max(0, basePrice - coupon.discount.value);
    }
  }
  return basePrice;
}

export function isDowngrade(formerPlan: Plan, newPlan: Plan): boolean {
  return planWeights[newPlan as keyof typeof planWeights] - planWeights[formerPlan as keyof typeof planWeights] < 0;
}

export function getDaysLeft(subscription: ISubscription | null): number {
  if (subscription === null) {
    return 0;
  }

  return Math.max(0, differenceInDays(subscription.subscriptionExpiresAt, endOfToday()) + 1);
}

export const hasManuallyIncreasedLicensePlanLimits = (
  licenseLimits: Pick<ILicenseSettings, keyof IPlanLimits>,
  standardLimits: IPlanLimits
): boolean => {
  return (Object.keys(standardLimits) as Array<keyof IPlanLimits>).some(
    key => (licenseLimits[key] ?? Infinity) > (standardLimits[key] ?? Infinity)
  );
};
